import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useMangoApi } from '~/atoms/MangoApi'
import { QueryCancellation, UseQueryParameters } from '~/types/ApiRequestTypes'
import { getOrderingString } from '~/utils/apiQueryUtils'

import {
  ApiError,
  Contact,
  ContactSource,
  ContactSourcesQueryParams,
  ContactsQueryParams,
  PaginatedApiResult,
} from '@mangovoice/types'

interface UseGetContactSourcesParams {
  pbxId: number
  onQuerySuccess?: (data: PaginatedApiResult<ContactSource>) => void
  onQueryError?: (queryParams: ContactsQueryParams, error?: ApiError) => void
  options?: Omit<
    UseQueryOptions<PaginatedApiResult<ContactSource>, ApiError>,
    'queryKey'
  >
}

interface UseGetContactSourcesQueryParams {
  search?: string
  offset?: number
  limit?: number
  ordering?: OrderingField<ContactSource>
}

/**
 * @description Get contact sources for a pbx - disabled by default - use refetch with params or set enabled to true in options
 * @param params - pbxId, onQuerySuccess, onQueryError, options
 * @returns
 */
export const useGetContactSources = (params: UseGetContactSourcesParams) => {
  const api = useMangoApi()
  const { pbxId, onQuerySuccess, onQueryError, options } = params
  const [queryParams, setQueryParams] =
    useState<UseGetContactSourcesQueryParams>()

  const refetch = (params: UseGetContactSourcesQueryParams) => {
    setQueryParams(params)
  }

  const query = useQuery<PaginatedApiResult<ContactSource>, ApiError>({
    queryKey: ['contactSources', pbxId, { ...queryParams }],
    queryFn: async () => {
      if (pbxId === 0) {
        return Promise.reject({ message: 'pbxId is required' })
      }

      const params: ContactSourcesQueryParams = {
        pbx_id: pbxId,
        search: queryParams?.search,
        offset: queryParams?.offset,
        limit: queryParams?.limit,
        ordering: queryParams?.ordering,
      }

      const { ok, data, status } = await api.getContactSources(params)

      if (!ok) {
        const error = {
          ...data,
          status,
        }
        onQueryError?.(params, error)
        return Promise.reject(error)
      }

      onQuerySuccess?.(data!)
      return data!
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: (options?.retry as number | boolean | undefined) ?? false,
  })

  useEffect(() => {
    if (queryParams) {
      query.refetch()
    }
  }, [queryParams])

  return {
    ...query,
    refetch,
  }
}

interface UseGetContactSourceParams {
  contactSourceId?: number
  onQuerySuccess?: (data: ContactSource) => void
  onQueryError?: (contactSourceId: number, error?: ApiError) => void
  options?: Omit<UseQueryOptions<ContactSource, ApiError>, 'queryKey'>
}
/**
 * @description Get a contact source by id - enabled by default
 * @param params
 * @returns
 */
export const useGetContactSource = (params: UseGetContactSourceParams) => {
  const api = useMangoApi()
  const [queryParams, setQueryParams] = useState<
    { contactSourceId: number } | undefined
  >(
    params?.contactSourceId
      ? { contactSourceId: params.contactSourceId }
      : undefined,
  )

  const { onQuerySuccess, onQueryError, options } = params

  const contactSourceId = queryParams?.contactSourceId ?? 0
  const refetch = (newContactSourceId: number) => {
    setQueryParams({ contactSourceId: newContactSourceId })
  }

  const query = useQuery<ContactSource, ApiError>({
    queryKey: ['contactSource', contactSourceId],
    queryFn: async () => {
      if (contactSourceId === 0) {
        return Promise.reject({ message: 'contactSourceId is required' })
      }

      const { ok, data, status } = await api.getContactSource(contactSourceId)

      if (!ok) {
        const error = {
          ...data,
          status,
        }
        onQueryError?.(contactSourceId, error)
        return Promise.reject(error)
      }

      onQuerySuccess?.(data!)
      return data!
    },
    ...options,
    retry: (options?.retry as number | boolean | undefined) ?? false,
  })

  useEffect(() => {
    if (queryParams?.contactSourceId) {
      query.refetch()
    }
  }, [queryParams])

  return {
    ...query,
    refetch,
  }
}

interface UseGetContactsParams
  extends UseQueryParameters<PaginatedApiResult<Contact>, ContactsQueryParams> {
  pbxId: number
}

interface UseGetContactsQueryParams {
  contactSourceIds?: number[]
  search?: string
  offset?: number
  limit?: number
  ordering?: OrderingField<Contact>[] | OrderingField<Contact>
}

export const useGetContacts = (params: UseGetContactsParams) => {
  const api = useMangoApi()
  const queryClient = useQueryClient()
  const {
    pbxId,
    onQueryStarted,
    onQuerySuccess,
    onQueryError,
    onQueryCancelled,
    options,
  } = params
  const [queryParams, setQueryParams] = useState<UseGetContactsQueryParams>()

  const getQueryKey = () => {
    const key = ['contacts', pbxId, { ...queryParams }]
    return key
  }
  const tryCancelQuery: QueryCancellation = () => {
    queryClient.cancelQueries({
      queryKey: getQueryKey(),
    })
  }

  const refetch = (params: UseGetContactsQueryParams) => {
    tryCancelQuery()
    setQueryParams(params)
  }

  const query = useQuery<PaginatedApiResult<Contact>, ApiError>({
    queryKey: getQueryKey(),
    queryFn: async ({ signal }) => {
      onQueryStarted?.()
      if (pbxId === 0) {
        onQueryError?.()
        return Promise.reject({ message: 'pbxId is required' })
      }

      const params: ContactsQueryParams = {
        pbx_id: pbxId,
        contact_source_ids: queryParams?.contactSourceIds?.join(','),
        search: queryParams?.search,
        offset: queryParams?.offset,
        limit: queryParams?.limit,
        ordering: getOrderingString(queryParams?.ordering),
      }

      const { ok, data, status } = await api.getContacts(params, signal)

      if (!ok) {
        const error = {
          ...data,
          status,
        }
        if (!signal?.aborted) {
          onQueryCancelled?.()
        }
        onQueryError?.(error, params)
        return Promise.reject(error)
      }

      onQuerySuccess?.(data!)
      return data!
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: (options?.retry as number | boolean | undefined) ?? false,
  })

  useEffect(() => {
    if (queryParams) {
      query.refetch()
    }
  }, [queryParams])

  return {
    ...query,
    refetch,
    tryCancelQuery,
  }
}

interface UseGetContactParams {
  contactId?: number
  onQuerySuccess?: (data: Contact) => void
  onQueryError?: (contactId: number, error?: ApiError) => void
  options?: Omit<UseQueryOptions<Contact, ApiError>, 'queryKey'>
}

/**
 * @description Get a contact by id - disabled by default - use refetch with params or set enabled to true in options
 * @param params
 * @returns
 */
export const useGetContact = (params: UseGetContactParams) => {
  const api = useMangoApi()
  const [queryParams, setQueryParams] = useState<
    { contactId: number } | undefined
  >(params?.contactId ? { contactId: params.contactId } : undefined)

  const { onQuerySuccess, onQueryError, options } = params

  const contactId = queryParams?.contactId ?? 0
  const refetch = (contactId: number) => {
    setQueryParams({ contactId })
  }

  const query = useQuery<Contact, ApiError>({
    queryKey: ['contact', contactId],
    queryFn: async () => {
      if (contactId === 0) {
        return Promise.reject({ message: 'contactId is required' })
      }

      const { ok, data, status } = await api.getContact(contactId)

      if (!ok) {
        const error = {
          ...data,
          status,
        }
        onQueryError?.(contactId, error)
        return Promise.reject(error)
      }

      onQuerySuccess?.(data!)
      return data!
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: (options?.retry as number | boolean | undefined) ?? false,
  })

  useEffect(() => {
    if (queryParams?.contactId) {
      query.refetch()
    }
  }, [queryParams])

  return {
    ...query,
    refetch,
  }
}
//commented out due to incomplete implementation
// interface ContactHistoryEntry {
//   entryType: 'message' | 'call' | 'fax' | 'voicemail'
//   entryId: number
//   entryDate: Date
//   entryFromNumber: string
//   entryToNumber: string
// }
// interface UseGetContactHistoryParams {
//   contactId: number
//   options?: UseQueryOptions<ContactHistoryEntry[], ApiError>
// }

// const transformMessageThreadToContactHistoryEntry = (
//   messageThread: MessageThread,
// ) => {
//   return {
//     entryType: 'message',
//     entryId: messageThread.id,
//     entryDate: messageThread.created_at,
//     entryFromNumber: messageThread.from_number,
//     entryToNumber: messageThread.to_number,
//   }
// }

// export const useGetContactHistory = (params: UseGetContactHistoryParams) => {
//   const api = useMangoApi()
//   const { contactId } = params

//   const query = useQuery<ContactHistoryEntry[], ApiError>(
//     ['contactRecentActivity', contactId],
//     async () => {
//       //get message threads by contact id - query for the 100 most recent
//       const contactMessagesPromise = api.getMessageThreads({
//         limit: 100,
//         contactId,
//       })

//       const callsPromisePlaceholder = new Promise<[]>((resolve) => {
//         return resolve([])
//       })

//       const results = await Promise.all([
//         contactMessagesPromise,
//         callsPromisePlaceholder,
//       ])
//       const {
//         ok: messageThreadsOk,
//         data: messageThreadsData,
//         status: messageThreadsStatus,
//       } = results[0]

//       if (!messageThreadsOk) {
//         return Promise.reject({
//           message: messageThreadsData?.message,
//           status: messageThreadsStatus,
//         })
//       }
//     },
//     {
//       enabled: contactId !== 0,
//       retry: false,
//     },
//   )
// }
